/**
 * What read-aloud says, and in which voice. DOM-free so it can be tested.
 *
 * The one rule that is not negotiable: on-device voices only. Browsers also list voices that run on
 * someone else's server ("Google UK English Female" in Chrome), and speaking with one sends the text —
 * the lesson, the tutor's reply to her — to that server. `localService` is the browser's own word for
 * "this runs here". When no on-device English voice exists the answer is null, and the page shows no
 * speaker buttons rather than buttons that do nothing.
 */

export interface VoiceLike {
  name: string;
  lang: string;
  localService: boolean;
}

const PREFERRED = ["en-gb", "en-us"];

export function pickVoice<V extends VoiceLike>(voices: readonly V[]): V | null {
  const local = voices.filter((voice) => voice.localService && normalLang(voice.lang).startsWith("en"));
  for (const lang of PREFERRED) {
    const match = local.find((voice) => normalLang(voice.lang) === lang);
    if (match) return match;
  }
  return local[0] ?? null;
}

function normalLang(lang: string): string {
  return lang.replace(/_/g, "-").toLowerCase();
}

/** Longest piece spoken in one utterance. Chrome stops speaking long utterances part-way through. */
const MAX_PIECE = 200;

/**
 * Text as a list of sentences: a sentence ends at . ! or ? (and any closing quote or bracket)
 * followed by a space, or at a line break — so "3.5" stays whole. A sentence longer than MAX_PIECE is broken at a comma or space, never inside
 * a word.
 */
export function sentences(text: string): string[] {
  return text
    .split(/\n+/)
    // The end may sit inside closing quotes or brackets: 'The sky is blue.' A: sky.
    .flatMap((line) => line.split(/(?<=[.!?]['"’”)\]]*)\s+/))
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .flatMap(bounded);
}

function bounded(sentence: string): string[] {
  const parts: string[] = [];
  let rest = sentence;
  while (rest.length > MAX_PIECE) {
    const window = rest.slice(0, MAX_PIECE + 1);
    let cut = window.lastIndexOf(", ");
    if (cut > 0) cut += 1;
    else cut = window.lastIndexOf(" ");
    if (cut <= 0) cut = MAX_PIECE;
    parts.push(rest.slice(0, cut).trim());
    rest = rest.slice(cut).trim();
  }
  if (rest) parts.push(rest);
  return parts;
}

/**
 * Parts read one after another, each ending in a pause. A part that already ends in punctuation
 * keeps it ("Why does it stop?"), one that doesn't gets a full stop, and empty parts are dropped.
 */
export function joinSpoken(parts: readonly (string | null | undefined)[]): string {
  return parts
    .map((part) => (part ?? "").trim())
    .filter((part) => part.length > 0)
    // An ending may sit inside closing quotes or brackets: 'The sky is blue.' already ends.
    .map((part) => (/[.!?:;]['"’”)\]]*$/.test(part) ? part : `${part}.`))
    .join(" ");
}

const LETTERS = ["A", "B", "C", "D"] as const;

/** Options as she sees them: the letter on the chip, then the label. Matches the choice block. */
export function lettered(labels: readonly string[]): string[] {
  return labels.map((label, index) => `${LETTERS[index] ?? String(index + 1)}: ${label}`);
}
